import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../config/api';
import { useToast } from '../context/ToastContext';
import { useDataCache } from '../context/DataCacheContext';

export default function useWorkflowList() {
  const { getCachedData, setCachedData } = useDataCache();
  const cached = getCachedData('workflows');
  const [workflows, setWorkflows] = useState(cached || []);
  const [loading, setLoading] = useState(!cached);
  const skipNextLoadingRef = useRef(!!cached);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newWorkflowName, setNewWorkflowName] = useState('');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const fetchWorkflows = useCallback(async () => {
    try {
      if (!skipNextLoadingRef.current) {
        setLoading(true);
      }
      const response = await apiRequest('/workflows/');
      if (response.ok) {
        const data = await response.json();
        setWorkflows(data);
        setCachedData('workflows', data);
      } else {
        throw new Error('Failed to fetch workflows');
      }
    } catch (error) {
      console.error('Error fetching workflows:', error);
      setError(error.message);
      showError('Failed to fetch workflows');
    } finally {
      setLoading(false);
      skipNextLoadingRef.current = false;
    }
  }, [showError, setCachedData]);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  const handleCreateWorkflow = async (e) => {
    e.preventDefault();
    if (!newWorkflowName.trim()) return;

    try {
      setCreating(true);
      const response = await apiRequest('/workflows/', {
        method: 'POST',
        body: JSON.stringify({ name: newWorkflowName }),
      });

      if (!response.ok) {
        throw new Error('Failed to create workflow');
      }

      const newWorkflow = await response.json();
      setShowCreateModal(false);
      setNewWorkflowName('');
      showSuccess('Workflow created successfully');
      navigate(`/workflows/${newWorkflow.id}/builder`);
    } catch (error) {
      console.error('Error creating workflow:', error);
      showError('Failed to create workflow');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteWorkflow = async (workflowId) => {
    if (!window.confirm('Are you sure you want to delete this workflow? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiRequest(`/workflows/${workflowId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setWorkflows(prev => prev.filter(w => w.id !== workflowId));
        showSuccess('Workflow deleted successfully');
      } else {
        throw new Error('Failed to delete workflow');
      }
    } catch (error) {
      console.error('Error deleting workflow:', error);
      showError('Failed to delete workflow');
    }
  };

  const handleToggleStatus = async (workflowId, newStatus) => {
    try {
      const response = await apiRequest(`/workflows/${workflowId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        const updated = await response.json();
        setWorkflows(prev => prev.map(w => w.id === workflowId ? { ...w, status: updated.status } : w));
        showSuccess(newStatus === 'active' ? 'Workflow activated' : 'Workflow deactivated');
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating workflow status:', error);
      showError('Failed to update workflow status');
    }
  };

  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  }, []);

  return {
    workflows,
    loading,
    error,
    showCreateModal,
    newWorkflowName,
    creating,
    setShowCreateModal,
    setNewWorkflowName,
    fetchWorkflows,
    handleCreateWorkflow,
    handleDeleteWorkflow,
    handleToggleStatus,
    formatDate,
  };
}
